import { FC, useEffect, useMemo, useState } from 'react'

import {
  type LiveChatSDKContextType,
  LiveChatSDKProvider,
  useLiveChatSDK
} from '@inno-sdk/livechat-sdk'
import { getIsInsideWindow } from 'shared/utils'

import {
  BRIDGE_EVENT,
  BRIDGE_KEY,
  FALLBACK_DELAY_MS
} from './LiveChatSDKBridge.constants'
import { BridgedLiveChatSDKProviderProps } from './LiveChatSDKBridge.types'
import { getParentContext } from './LiveChatSDKBridge.utils'

const ExposeContext = () => {
  const context = useLiveChatSDK()

  useEffect(() => {
    if (typeof window === 'undefined' || !context) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any)[BRIDGE_KEY] = context

    for (let i = 0; i < window.frames.length; i++) {
      try {
        window.frames[i].postMessage(
          { type: BRIDGE_EVENT },
          window.location.origin
        )
      } catch {
        // ignore cross-origin frames
      }
    }
  }, [context])

  return null
}

/**
 * Reuses the parent window's Live Chat SDK instance when present (in iframes),
 * otherwise falls back to creating its own instance.
 */
export const BridgedLiveChatSDKProvider: FC<
  BridgedLiveChatSDKProviderProps
> = ({ user, children }) => {
  const initialContext = useMemo(() => getParentContext(), [])
  const [inheritedContext, setInheritedContext] = useState<
    LiveChatSDKContextType | undefined
  >(() => initialContext)
  const [waitingForParent, setWaitingForParent] = useState(
    getIsInsideWindow() && !initialContext
  )

  useEffect(() => {
    if (!getIsInsideWindow()) return

    const updateInheritedContext = () => {
      const ctx = getParentContext()
      if (ctx) {
        setInheritedContext(ctx)
        setWaitingForParent(false)
      }
    }

    const fallbackTimer = window.setTimeout(() => {
      setWaitingForParent(false)
    }, FALLBACK_DELAY_MS)

    updateInheritedContext()

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === BRIDGE_EVENT) {
        updateInheritedContext()
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
      clearTimeout(fallbackTimer)
    }
  }, [])

  if (waitingForParent) return null

  return (
    <LiveChatSDKProvider user={user} instance={inheritedContext?.livechat}>
      <ExposeContext />
      {children}
    </LiveChatSDKProvider>
  )
}
